import { LitElement, html, css } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';

interface SampleImage {
  id: string;
  url: string;
  title: string;
  prompt: string;
  style: string;
}

@customElement('image-carousel')
export class ImageCarousel extends LitElement {
  @property({ type: Array })
  images: SampleImage[] = [
    {
      id: '1',
      url: '/static/images/difflora/portrait_00072_.png',
      title: 'CYBERPUNK_PORTRAIT',
      prompt: 'professional portrait, cyberpunk style, neon lighting, futuristic clothing',
      style: 'Portrait'
    },
    {
      id: '2',
      url: '/static/images/difflora/anime2_00031_.png',
      title: 'ANIME_CHARACTER',
      prompt: 'anime style character, detailed eyes, expressive face, colorful hair',
      style: 'Character'
    },
    {
      id: '3',
      url: '/static/images/difflora/portrait_00060_.png',
      title: 'PHOTOREALISTIC_PORTRAIT',
      prompt: 'photorealistic portrait, natural lighting, professional photography',
      style: 'Portrait'
    },
    {
      id: '4',
      url: '/static/images/difflora/art_00001_.png',
      title: 'ABSTRACT_ART',
      prompt: 'abstract digital art, flowing geometric patterns, vibrant gradients',
      style: 'Art'
    },
    {
      id: '5',
      url: '/static/images/difflora/city_00001_.png',
      title: 'FUTURISTIC_CITYSCAPE',
      prompt: 'futuristic cityscape, towering skyscrapers, holographic displays',
      style: 'Landscape'
    },
    {
      id: '6',
      url: '/static/images/difflora/product-photo_00001_.png',
      title: 'PRODUCT_PHOTOGRAPHY',
      prompt: 'product photography, minimalist composition, professional lighting',
      style: 'Product'
    },
    {
      id: '7',
      url: '/static/images/difflora/fantasy-creature_00001_.png',
      title: 'FANTASY_CREATURE',
      prompt: 'fantasy creature, mythical being, detailed textures, magical atmosphere',
      style: 'Character'
    },
    {
      id: '8',
      url: '/static/images/difflora/ethereal_00002_.png',
      title: 'ETHEREAL_NATURE',
      prompt: 'ethereal nature scene, mystical forest, soft lighting, magical atmosphere',
      style: 'Landscape'
    }
  ];

  @state()
  private currentIndex = 0;

  @state()
  private isAutoPlaying = true;

  private autoPlayInterval?: number;

  static styles = css`
    :host {
      display: block;
      max-width: 100%;
    }

    .carousel-container {
      position: relative;
      border-radius: 1rem;
      overflow: hidden;
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 212, 255, 0.3);
    }

    .carousel-header {
      padding: 1.5rem;
      text-align: center;
      border-bottom: 1px solid rgba(0, 212, 255, 0.2);
    }

    .carousel-title {
      font-size: 1.5rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      margin-bottom: 0.5rem;
    }

    .carousel-subtitle {
      color: rgba(226, 232, 240, 0.7);
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.875rem;
    }

    .carousel-wrapper {
      position: relative;
      aspect-ratio: 16/9;
      overflow: hidden;
    }

    .carousel-track {
      display: flex;
      transform: translateX(var(--translate, 0));
      transition: transform 0.5s ease-in-out;
      height: 100%;
    }

    .carousel-slide {
      flex: 0 0 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(45deg, #0a0f1b, #1a1d29);
    }

    .sample-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      opacity: 0.8;
    }

    .placeholder-content {
      text-align: center;
      padding: 2rem;
      color: rgba(226, 232, 240, 0.6);
    }

    .placeholder-icon {
      font-size: 4rem;
      margin-bottom: 1rem;
      animation: float 6s ease-in-out infinite;
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }

    .slide-overlay {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
      padding: 2rem 1.5rem 1.5rem;
      color: white;
    }

    .slide-title {
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      font-size: 1.125rem;
      color: #00d4ff;
      margin-bottom: 0.5rem;
    }

    .slide-prompt {
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.875rem;
      color: rgba(226, 232, 240, 0.8);
      margin-bottom: 0.5rem;
      line-height: 1.4;
    }

    .slide-style {
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.75rem;
      color: #00ff88;
      background: rgba(0, 255, 136, 0.1);
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      display: inline-block;
    }

    .carousel-nav {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 212, 255, 0.2);
      border: 1px solid rgba(0, 212, 255, 0.5);
      color: #00d4ff;
      padding: 0.75rem;
      border-radius: 50%;
      cursor: pointer;
      transition: all 0.2s;
      backdrop-filter: blur(10px);
    }

    .carousel-nav:hover {
      background: rgba(0, 212, 255, 0.3);
      transform: translateY(-50%) scale(1.1);
    }

    .nav-prev {
      left: 1rem;
    }

    .nav-next {
      right: 1rem;
    }

    .carousel-dots {
      display: flex;
      justify-content: center;
      gap: 0.5rem;
      padding: 1.5rem;
    }

    .dot {
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;
      background: rgba(0, 212, 255, 0.3);
      cursor: pointer;
      transition: all 0.2s;
    }

    .dot.active {
      background: #00d4ff;
      box-shadow: 0 0 10px #00d4ff;
    }

    .dot:hover {
      background: #00d4ff;
    }

    .carousel-controls {
      display: flex;
      justify-content: center;
      gap: 1rem;
      padding: 1rem 1.5rem;
      border-top: 1px solid rgba(0, 212, 255, 0.2);
    }

    .control-button {
      background: none;
      border: 1px solid rgba(0, 212, 255, 0.5);
      color: #00d4ff;
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.2s;
    }

    .control-button:hover {
      background: rgba(0, 212, 255, 0.1);
    }

    .control-button.active {
      background: rgba(0, 212, 255, 0.2);
    }

    .cyber-green { color: #00ff88; }
    .cyber-yellow { color: #ffd700; }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.startAutoPlay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAutoPlay();
  }

  private startAutoPlay() {
    if (this.isAutoPlaying) {
      this.autoPlayInterval = window.setInterval(() => {
        this.nextSlide();
      }, 4000);
    }
  }

  private stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
    }
  }

  private toggleAutoPlay() {
    this.isAutoPlaying = !this.isAutoPlaying;
    if (this.isAutoPlaying) {
      this.startAutoPlay();
    } else {
      this.stopAutoPlay();
    }
  }

  private nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.requestUpdate();
  }

  private prevSlide() {
    this.currentIndex = this.currentIndex === 0 ? this.images.length - 1 : this.currentIndex - 1;
    this.requestUpdate();
  }

  private goToSlide(index: number) {
    this.currentIndex = index;
    this.stopAutoPlay();
    this.requestUpdate();
  }

  render() {
    const translateX = -this.currentIndex * 100;

    return html`
      <div class="carousel-container">
        <div class="carousel-header">
          <h3 class="carousel-title">
            <span class="cyber-green">[</span>SAMPLE_GENERATIONS<span class="cyber-green">]</span>
          </h3>
          <p class="carousel-subtitle">
            <span class="cyber-yellow">[SHOWCASE]</span> See what's possible with our platform
          </p>
        </div>

        <div class="carousel-wrapper">
          <div class="carousel-track" style="--translate: ${translateX}%">
            ${this.images.map(image => html`
              <div class="carousel-slide">
                <img src="${image.url}" alt="${image.title}" class="sample-image" />
                <div class="slide-overlay">
                  <div class="slide-title">${image.title}</div>
                  <div class="slide-prompt">"${image.prompt}"</div>
                  <div class="slide-style">${image.style}</div>
                </div>
              </div>
            `)}
          </div>

          <button class="carousel-nav nav-prev" @click=${this.prevSlide}>
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>

          <button class="carousel-nav nav-next" @click=${this.nextSlide}>
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>

        <div class="carousel-dots">
          ${this.images.map((_, index) => html`
            <div
              class="dot ${index === this.currentIndex ? 'active' : ''}"
              @click=${() => this.goToSlide(index)}>
            </div>
          `)}
        </div>

        <div class="carousel-controls">
          <button
            class="control-button ${this.isAutoPlaying ? 'active' : ''}"
            @click=${this.toggleAutoPlay}>
            ${this.isAutoPlaying ? '⏸️ PAUSE' : '▶️ PLAY'}
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'image-carousel': ImageCarousel;
  }
}
